import React, { useState } from 'react';
import { ChevronRightIcon, ImageOffIcon, ListChecksIcon, PlusIcon, Trash2Icon } from 'lucide-react';
import { QualityReport } from '../types/quality';
import { getStats } from '../utils/quality';
import { ProgressRing } from '../components/quality/ProgressRing';
import { NewQualityReportModal } from '../components/quality/NewQualityReportModal';
import { Customer } from '../components/CustomerCard';
interface QualityAssuranceProps {
  reports: QualityReport[];
  customers: Customer[];
  onOpenReport: (reportId: string) => void;
  onCreateReport: (report: Omit<QualityReport, 'id'>) => void;
  onDeleteReport: (report: QualityReport) => void;
  nextReportId: string;
  responsible: {
    name: string;
    email: string;
    phone: string;
  };
  canEdit: boolean;
}
export function QualityAssurance({
  reports,
  customers,
  onOpenReport,
  onCreateReport,
  onDeleteReport,
  nextReportId,
  responsible,
  canEdit
}: QualityAssuranceProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const open = reports.filter(r => r.status === 'Åben');
  const totalMissing = reports.reduce((sum, r) => sum + getStats(r).missingPhoto, 0);
  return <div data-id="element-1851" data-mp="27jxzye2w517">
      <div className="mb-8 flex flex-wrap items-start justify-between gap-4" data-id="element-1852" data-mp="27tnzlz4ic6w">
        <div data-id="element-1853" data-mp="29wbnzcszt8g">
          <h1 className="text-3xl font-bold tracking-tight text-stone-900" data-id="element-1854" data-mp="76ef8vwedlj7">
            Kvalitetssikring
          </h1>
          <p className="mt-2 text-stone-500" data-id="element-1855" data-mp="3omw9s5g4gxd5">
            {open.length} åbne rapporter
            {totalMissing > 0 && ` · ${totalMissing} kontrolpunkter mangler foto`}
          </p>
        </div>
        {canEdit && <button onClick={() => setIsModalOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1856" data-mp="3foawoptf6ajr">
            <PlusIcon className="h-4 w-4" data-id="element-1857" data-mp="3an3parv6vddm" />
            Ny kvalitetssikring
          </button>}
      </div>

      {reports.length === 0 ? <div className="rounded-2xl border border-dashed border-stone-300 bg-white px-6 py-16 text-center" data-id="element-1858" data-mp="1g8ptv0l4o8b9">
          <ListChecksIcon className="mx-auto h-8 w-8 text-stone-300" data-id="element-1859" data-mp="1bf539tlv6gm3" />
          <h2 className="mt-3 text-base font-semibold text-stone-900" data-id="element-1860" data-mp="17fw9ioqtqiqj">
            Ingen kvalitetssikringer endnu
          </h2>
          <p className="mt-1 text-sm text-stone-500" data-id="element-1861" data-mp="12hkzoe0j488b">
            Opret en rapport for at dokumentere arbejdet med foto.
          </p>
          {canEdit && <button onClick={() => setIsModalOpen(true)} className="mt-5 inline-flex items-center gap-2 rounded-lg bg-brand-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-brand-700" data-id="element-1862" data-mp="1v45sliwnksdz">
              <PlusIcon className="h-4 w-4" data-id="element-1863" data-mp="1l8lxgypwxxqa" />
              Ny kvalitetssikring
            </button>}
        </div> : <ul className="space-y-3" data-id="element-1864" data-mp="9qje9v68tggc">
          {reports.map(report => {
        const stats = getStats(report);
        return <li key={report.id} className="flex items-center gap-2 rounded-2xl border border-stone-200 bg-white pr-3 transition-colors hover:border-brand-300" data-id="element-1865" data-mp="3vud225lovszr">
                <button onClick={() => onOpenReport(report.id)} className="flex min-w-0 flex-1 items-center gap-4 rounded-2xl p-5 text-left" data-id="element-1866" data-mp="9j8tel4i0ai1">
                  <ProgressRing percent={stats.percent} size={52} data-id="element-1867" data-mp="iddnreiuc42b" />

                  <span className="min-w-0 flex-1" data-id="element-1868" data-mp="nboxlp94yekj">
                    <span className="flex flex-wrap items-center gap-2" data-id="element-1869" data-mp="sbtidrh2tvgk">
                      <span className="font-mono text-xs font-semibold text-brand-700" data-id="element-1870" data-mp="scl0q0lnxwow">
                        {report.reportId}
                      </span>
                      <span className="inline-flex items-center gap-1.5 rounded-full bg-stone-100 px-2 py-0.5 text-xs font-medium text-stone-600" data-id="element-1871" data-mp="2u3y8qyixgkp6">
                        <span className={`h-1.5 w-1.5 rounded-full ${report.status === 'Åben' ? 'bg-emerald-500' : 'bg-stone-400'}`} data-id="element-1872" data-mp="2z4dkjlnk9k51" />
                        {report.status}
                      </span>
                    </span>
                    <span className="mt-1 block truncate text-base font-semibold text-stone-900" data-id="element-1873" data-mp="xdhkar00vpto">
                      {report.title}
                    </span>
                    <span className="mt-0.5 block truncate text-sm text-stone-500" data-id="element-1874" data-mp="5pfr3fp5j1yr">
                      {report.customer.name} · {stats.completedPoints}/
                      {stats.totalPoints} kontrolpunkter ·{' '}
                      {report.photos.length} fotos
                    </span>
                  </span>

                  {stats.missingPhoto > 0 && <span className="hidden flex-shrink-0 items-center gap-1.5 rounded-lg bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-700 sm:inline-flex" data-id="element-1875" data-mp="3i630717vegf1">
                      <ImageOffIcon className="h-3.5 w-3.5" data-id="element-1876" data-mp="1xlo1ftf462x8" />
                      {stats.missingPhoto} mangler foto
                    </span>}

                  <ChevronRightIcon className="h-5 w-5 flex-shrink-0 text-stone-300" data-id="element-1877" data-mp="1oodp04qk87qq" />
                </button>

                {canEdit && <button onClick={() => onDeleteReport(report)} aria-label={`Slet ${report.reportId}`} className="flex-shrink-0 rounded-lg p-2 text-stone-300 transition-colors hover:bg-red-50 hover:text-red-600" data-id="element-1878" data-mp="1tjsykkhjbhhc">
                    <Trash2Icon className="h-4 w-4" data-id="element-1879" data-mp="27fy4q65lglmq" />
                  </button>}
              </li>;
      })}
        </ul>}

      <NewQualityReportModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onCreate={onCreateReport} customers={customers} nextReportId={nextReportId} responsible={responsible} data-id="element-1880" data-mp="vxnalg9d3948" />
    </div>;
}